import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CallQaAgentsPage: React.FC = () => {
  const { user } = useAuth();
  const canRead = (user?.permissions || []).includes('call_qa.scoring.read');
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['callQaAgents'],
    queryFn: () => apiClient.callQa.listAgents(),
    enabled: canRead,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['callQaAgentDetail', selected],
    queryFn: () => apiClient.callQa.getAgent(String(selected)),
    enabled: canRead && Boolean(selected),
  });

  if (!canRead) {
    return <Alert severity="warning">Agent QA requires call_qa.scoring.read.</Alert>;
  }

  const rows = data?.data?.data?.rows || [];
  const sampleNote = data?.data?.data?.sample_note;
  const detail = detailData?.data?.data;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Agent QA summaries from scored calls. Small samples are flagged.
      </Typography>
      {sampleNote && <Alert severity="info" sx={{ mb: 2 }}>{sampleNote}</Alert>}

      {isLoading ? <CircularProgress size={24} /> : (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Agent', 'Calls', 'Scored', 'Average QA', 'Critical Failures', 'Needing Review', ''].map((h) => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No scored agents yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((r: any) => (
                <TableRow key={r.employee_id} hover selected={selected === r.employee_id}>
                  <TableCell>{r.employee_name}</TableCell>
                  <TableCell>{r.calls}</TableCell>
                  <TableCell>{r.scored_calls}</TableCell>
                  <TableCell>
                    {r.average_qa != null ? r.average_qa : '—'}
                    {r.sample_insufficient && <Chip size="small" label="small sample" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>{r.critical_failures}</TableCell>
                  <TableCell>{r.needing_review}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => setSelected(r.employee_id)}>Open</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {selected && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle1" gutterBottom>Agent Detail</Typography>
          {detailLoading || !detail ? <CircularProgress size={20} /> : (
            <>
              <Typography variant="body2">
                Average QA: {detail.average_qa ?? '—'}
                {detail.sample_insufficient ? ' (insufficient sample)' : ''} · Scored: {detail.scored_calls}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Category averages</Typography>
              {(detail.category_averages || []).map((c: any) => (
                <Typography key={c.section} variant="body2">
                  {c.section}: {c.average ?? '—'} (n={c.sample_size})
                </Typography>
              ))}
              <Typography variant="subtitle2" sx={{ mt: 2 }} data-testid="agent-coaching-opportunities">
                Recurring Coaching Opportunities
              </Typography>
              {detail.coaching_sample_insufficient ? (
                <Typography variant="body2" color="text.secondary">
                  Insufficient sample (n={detail.coaching_sample_size || 0}) — trends not shown.
                </Typography>
              ) : (detail.recurring_coaching_opportunities || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">No recurring opportunities detected.</Typography>
              ) : (detail.recurring_coaching_opportunities || []).map((o: any) => (
                <Typography key={o.category} variant="body2">
                  {o.category}: {o.opportunity_rate}% weak ({o.fail_or_partial_count}/{o.scored_count})
                </Typography>
              ))}
              {(detail.coaching_history || []).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>Coaching history</Typography>
                  {(detail.coaching_history || []).slice(0, 5).map((c: any) => (
                    <Box key={c.id} display="flex" gap={1} alignItems="center">
                      <Typography variant="body2">{c.topic} · {c.status}</Typography>
                      <Button size="small" onClick={() => navigate(`/call-qa/coaching/${c.id}`)}>Open</Button>
                    </Box>
                  ))}
                </>
              )}
              <Typography variant="subtitle2" sx={{ mt: 2 }}>Recent scored calls</Typography>
              {(detail.recent_calls || []).filter((c: any) => c.final_overall_score != null).slice(0, 10).map((c: any) => (
                <Box key={c.call_id} display="flex" gap={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    {moment(c.started_at).format('MMM D h:mm A')} · {c.final_overall_score} · {c.final_overall_status}
                    {c.critical_failure_count > 0 ? ' · CRITICAL' : ''}
                  </Typography>
                  <Button size="small" onClick={() => navigate(`/call-qa/calls/${c.call_id}`)}>Review</Button>
                </Box>
              ))}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CallQaAgentsPage;
